import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { RootState } from "src/setup/redux/store";
import ChatItem from "./ChatItem";
import { useTranslation } from "react-i18next";
import { fetchChatList } from "src/app/modules/chat/redux/ChatThunk";
import {
  setSelectedCollaboration,
  setSelectedRoom,
} from "src/app/modules/chat/redux/ChatSlice.v2";

const ChatList: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [scrollHeight, setScrollHeight] = useState<number>(0);

  // Track if we've already attempted a fetch for each tab to prevent infinite loops
  const fetchAttemptedRef = useRef<Record<string, boolean>>({});

  const { filter, activeTab } = useSelector(
    (state: RootState) => state.ChatV2,
    shallowEqual
  );
  const {
    collaborationOrder,
    collaborationsById,
    hasMore,
    isLoading,
    selectedCollaborationId,
    selectedRoomId,
  } = useSelector(
    (state: RootState) =>
      state.ChatV2[activeTab] ?? {
        collaborationOrder: [],
        collaborationsById: {},
        hasMore: false,
        isLoading: false,
        selectedCollaborationId: null,
        selectedRoomId: null,
      },
    shallowEqual
  );

  useEffect(() => {
    // Only fetch if:
    // 1. No data yet (collaborationOrder is empty)
    // 2. Not currently loading
    // 3. Haven't already attempted a fetch for this tab (prevents infinite loop when no data exists)
    const hasAttempted = fetchAttemptedRef.current[activeTab];

    if (collaborationOrder.length === 0 && !isLoading && !hasAttempted) {
      fetchAttemptedRef.current[activeTab] = true;
      dispatch(fetchChatList({ chatType: activeTab, lastInteractionAt: null }));
    }
  }, [activeTab, dispatch, collaborationOrder.length, isLoading]);

  // 🔁 Scroll handling for pagination
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      setScrollHeight(scrollTop);
      if (
        scrollTop + clientHeight >= scrollHeight - 25 &&
        hasMore &&
        !isLoading
      ) {
        const lastId = collaborationOrder[collaborationOrder.length - 1];
        const lastCollab = lastId && collaborationsById[lastId];
        if (lastCollab?.lastInteractionAt) {
          dispatch(
            fetchChatList({
              chatType: activeTab,
              lastInteractionAt: lastCollab.lastInteractionAt,
            })
          );
        }
      }
    },
    [
      collaborationOrder,
      collaborationsById,
      hasMore,
      isLoading,
      dispatch,
      activeTab,
    ]
  );

  // 🔍 Filtering based on `filter`
  const chatList = useMemo(() => {
    return collaborationOrder
      .map((id) => collaborationsById[id])
      .filter((c) => {
        if (!filter) return true;
        const name = c.profileName?.toLowerCase() ?? "";
        const channel = c.lastInteractionChannel;
        const buyerId = c.userSearchKeys?.[channel]?.toLowerCase() ?? "";
        return name.includes(filter) || buyerId.includes(filter);
      });
  }, [collaborationOrder, collaborationsById, filter]);

  // ⬆️ Scroll to top
  const handleTop = useCallback(() => {
    const container = document.getElementById("chatlist");
    container?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const chatOpenHandler = (collaborationId: string, roomId: string) => {
    dispatch(
      setSelectedCollaboration({
        chatType: activeTab,
        id: collaborationId,
      })
    );
    dispatch(
      setSelectedRoom({
        chatType: activeTab,
        id: roomId,
      })
    );
  };

  return (
    <div
      id="kt_chat_contacts_body"
      data-testid="chat-contacts-body"
      className="position-relative flex-grow-1 d-flex flex-column"
      style={{ overflow: "hidden", minHeight: 0 }}
    >
      <div
        id="chatlist"
        onScroll={handleScroll}
        className="w-100 flex-grow-1 overflow-y-auto"
        style={{ minHeight: 0 }}
      >
        {/* Loader */}
        {isLoading && chatList.length === 0 && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: 50 }}
          >
            <span className="spinner-border spinner-border-md m-2"></span>
          </div>
        )}

        {/* Chat Items */}
        {chatList.length > 0
          ? chatList.map((chatItem) => (
              <div
                key={chatItem.id}
                onClick={() =>
                  chatOpenHandler(chatItem?.id, chatItem?.roomsModel?.[0]?.id)
                }
              >
                <ChatItem
                  item={chatItem}
                  selected={chatItem.id === selectedCollaborationId}
                />
              </div>
            ))
          : !isLoading && (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "70vh" }}
              >
                <h4 className="text-muted">{t("Common.NoData")}</h4>
              </div>
            )}

        {/* loader  */}
        {isLoading && chatList.length !== 0 && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: 50 }}
          >
            <span className="spinner-border spinner-border-md m-2"></span>
          </div>
        )}

        {/* Reached End */}
        {!hasMore && chatList.length > 0 && (
          <div className="text-center text-muted py-3">
            {t("Common.ReachedEnd")}
          </div>
        )}

        {/* Scroll-to-top button */}
        {scrollHeight > 10 && (
          <button
            className="position-absolute rounded-circle p-3 bg-primary border-0"
            style={{ right: 10, bottom: 10 }}
            onClick={handleTop}
            aria-label="Scroll to top"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              className="text-light"
            >
              <path
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                d="M9 19v-7H5.483a.2.2 0 0 1-.142-.341L12 5l6.659 6.659a.2.2 0 0 1-.142.341H15v7"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default ChatList;
